// ============================================
// COMPANYDIAGNOSTICFORMSPAGE.JSX - Company Diagnostic Forms Sub-Page
// ============================================

import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetCompanyByIdQuery,
  useGetCompanyMembersQuery,
  useGetCompanyDiagnosticAssignmentsQuery,
  useCancelDiagnosticAssignmentMutation,
  useGetDiagnosticSubmissionByIdQuery,
  useGetCompanyDiagnosticTemplatesQuery,
  useCreateAdminDiagnosticRequestMutation,
  useApproveDiagnosticRequestMutation,
  useGetTargetFeedbackWithStatsQuery,
} from "../../../services/api";
import DiagnosticSection from "./DiagnosticSection";
import { IconCircle } from "./components/Icons";
import MetricsGrid from "./components/MetricsGrid";
import DiagnosticAssignmentsTable from "./components/DiagnosticAssignmentsTable";
import MemberResultsView from "./components/MemberResultsView";
import "./CompanyDetails.css";

/**
 * CompanyDiagnosticFormsPage Component
 * Sub-page for managing company diagnostic forms and assignments
 */
export default function CompanyDiagnosticFormsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // ==================== API QUERIES ====================
  const { data: companyData } = useGetCompanyByIdQuery(id);
  const companyName = companyData?.name || "Loading...";

  // Get employees for DiagnosticSection
  const { data: membersData } = useGetCompanyMembersQuery({
    id,
    page: 1,
    limit: 100, // Get all employees for assignment dropdown
    includeTestMembers: true, // Include the admin "View-as" test member so it can be assigned diagnostics
  });
  const employees = membersData?.members || [];

  // Get diagnostic assignments
  const { data: assignmentsData, refetch: refetchAssignments } =
    useGetCompanyDiagnosticAssignmentsQuery({ companyId: id });
  const diagnosticAssignments = assignmentsData?.assignments || [];

  // Get diagnostic form templates available to this company (for the assign dropdown)
  const { data: companyTemplates = [] } = useGetCompanyDiagnosticTemplatesQuery(id, {
    skip: !id,
  });
  // Backend only accepts published templates for assignment
  const publishedTemplates = companyTemplates.filter(
    (t) => t.status === "published"
  );

  // ==================== MUTATIONS ====================
  const [cancelDiagnosticAssignment] = useCancelDiagnosticAssignmentMutation();
  const [createAdminRequest, { isLoading: isAssigning }] =
    useCreateAdminDiagnosticRequestMutation();
  const [approveDiagnosticRequest, { isLoading: isApprovingRequest }] =
    useApproveDiagnosticRequestMutation();

  // ==================== UI STATE ====================
  const [answersModal, setAnswersModal] = useState({ isOpen: false, assignment: null });
  const [assignModal, setAssignModal] = useState(false);
  // Assign-a-form-to-a-pending-request modal (approves an employee-initiated request)
  const [assignFormModal, setAssignFormModal] = useState({ isOpen: false, request: null });
  const [assignFormTemplateId, setAssignFormTemplateId] = useState("");
  const [assignForm, setAssignForm] = useState({
    templateId: "",
    targetId: "",
    diagnoserIds: [],
    includeSelfDiagnosis: true,
  });

  // Per-person aggregated results: selected employee being viewed
  const [selectedTargetId, setSelectedTargetId] = useState("");
  const { data: targetReport, isFetching: reportLoading } =
    useGetTargetFeedbackWithStatsQuery(
      { targetId: selectedTargetId, companyId: id },
      { skip: !selectedTargetId }
    );

  // Fetch submission when answers modal is open
  const submissionId = answersModal.assignment?.submission?._id || answersModal.assignment?.submission;
  const { data: submissionData, isLoading: submissionLoading } = useGetDiagnosticSubmissionByIdQuery(
    submissionId,
    { skip: !answersModal.isOpen || !submissionId }
  );

  // ==================== HANDLERS ====================
  const handleCancelDiagnosticAssignment = async (assignmentId) => {
    if (!window.confirm("Are you sure you want to cancel this assignment?")) return;
    try {
      await cancelDiagnosticAssignment(assignmentId).unwrap();
      refetchAssignments();
    } catch (error) {
      console.error("Failed to cancel assignment:", error);
    }
  };

  const handleOpenAnswersModal = (assignment) => {
    setAnswersModal({ isOpen: true, assignment });
  };

  const handleCloseAnswersModal = () => {
    setAnswersModal({ isOpen: false, assignment: null });
  };

  // ===== Assign a form to a pending employee request (approve) =====
  const handleOpenAssignFormModal = (request) => {
    setAssignFormTemplateId("");
    setAssignFormModal({ isOpen: true, request });
  };

  const handleCloseAssignFormModal = () => {
    setAssignFormModal({ isOpen: false, request: null });
    setAssignFormTemplateId("");
  };

  const handleConfirmAssignForm = async () => {
    if (!assignFormTemplateId) {
      alert("Please select a diagnostic form");
      return;
    }
    try {
      await approveDiagnosticRequest({
        requestId: assignFormModal.request._id,
        templateId: assignFormTemplateId,
      }).unwrap();
      refetchAssignments();
      handleCloseAssignFormModal();
    } catch (error) {
      console.error("Failed to assign form to request:", error);
      alert(
        error?.data?.error?.message ||
          error?.data?.message ||
          "Failed to assign form to this request"
      );
    }
  };

  // ==================== ASSIGN-TO-INDIVIDUAL HANDLERS ====================
  const handleOpenAssignModal = () => {
    setAssignForm({
      templateId: "",
      targetId: "",
      diagnoserIds: [],
      includeSelfDiagnosis: true,
    });
    setAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setAssignModal(false);
  };

  const memberId = (m) => m._id || m.id;

  const toggleDiagnoser = (userId) => {
    setAssignForm((prev) => ({
      ...prev,
      diagnoserIds: prev.diagnoserIds.includes(userId)
        ? prev.diagnoserIds.filter((d) => d !== userId)
        : [...prev.diagnoserIds, userId],
    }));
  };

  const handleConfirmAssign = async () => {
    if (!assignForm.targetId) {
      alert("Please select an employee to assign the form to");
      return;
    }
    if (!assignForm.templateId) {
      alert("Please select a diagnostic form");
      return;
    }
    if (assignForm.diagnoserIds.length === 0 && !assignForm.includeSelfDiagnosis) {
      alert("Please select at least one diagnoser or include self-diagnosis");
      return;
    }

    try {
      await createAdminRequest({
        companyId: id,
        targetId: assignForm.targetId,
        diagnoserIds: assignForm.diagnoserIds,
        templateId: assignForm.templateId,
        includeSelfDiagnosis: assignForm.includeSelfDiagnosis,
      }).unwrap();
      // The mutation invalidates the DiagnosticAssignment tag, so the table refreshes.
      refetchAssignments();
      handleCloseAssignModal();
    } catch (error) {
      console.error("Failed to assign diagnostic:", error);
      alert(
        error?.data?.error?.message ||
          error?.data?.message ||
          "Failed to create diagnostic request"
      );
    }
  };

  // Get status badge style
  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: "#fef3c7", color: "#d97706", label: "Pending" },
      in_progress: { bg: "#dbeafe", color: "#2563eb", label: "In Progress" },
      completed: { bg: "#dcfce7", color: "#16a34a", label: "Completed" },
      expired: { bg: "#fee2e2", color: "#dc2626", label: "Expired" },
    };
    return styles[status] || styles.pending;
  };

  // Format answer value based on question type
  const formatAnswerValue = (answer) => {
    if (answer.value === null || answer.value === undefined || answer.value === "") {
      return <span className="cd-answer-empty">No answer provided</span>;
    }
    switch (answer.questionType) {
      case "single_choice":
        return answer.valueLabel || answer.value;
      case "multi_choice":
        if (answer.valueLabels && answer.valueLabels.length > 0) {
          return answer.valueLabels.join(", ");
        }
        if (Array.isArray(answer.value)) {
          return answer.value.join(", ");
        }
        return answer.value;
      case "rating_scale":
        return `${answer.value} / 5`;
      case "checkbox":
        return answer.value ? "Yes" : "No";
      default:
        return answer.value;
    }
  };

  // ==================== METRICS ====================
  const metrics = [
    { icon: <IconCircle />, label: "Diagnostic Assignments", value: diagnosticAssignments.length.toString() },
  ];

  // ==================== RENDER ====================
  return (
    <>
      <div className="cd-company-details">
        <div className="welcome" onClick={() => navigate(`/admin/companies/${id}`)}>
          ← Back to {companyName}
        </div>
        <h1 className="username">{companyName} - Diagnostic Forms</h1>

        <MetricsGrid metrics={metrics} />

        <DiagnosticSection companyId={id} employees={employees} />

        {/* Per-person aggregated diagnostic results */}
        <div className="cd-card-block" style={{ marginTop: "16px" }}>
          <div className="cd-section-title-main">Employee Diagnostic Results</div>
          <div className="cd-assign-field" style={{ maxWidth: "360px" }}>
            <label className="cd-assign-label">Select an employee</label>
            <select
              className="cd-select"
              value={selectedTargetId}
              onChange={(e) => setSelectedTargetId(e.target.value)}
            >
              <option value="">-- Select an employee --</option>
              {employees.map((m) => (
                <option key={memberId(m)} value={memberId(m)}>
                  {m.firstName} {m.lastName}
                </option>
              ))}
            </select>
          </div>

          {selectedTargetId && (
            <MemberResultsView
              aggregatedData={targetReport}
              isLoading={reportLoading}
              targetName={(() => {
                const m = employees.find((e) => memberId(e) === selectedTargetId);
                return m ? `${m.firstName} ${m.lastName}` : "";
              })()}
            />
          )}
        </div>

        <div className="cd-section-header-simple">
          <div className="cd-section-title-main">Diagnostic Assignments</div>
          <button className="cd-add-btn" onClick={handleOpenAssignModal}>
            + Assign to individual
          </button>
        </div>

        <DiagnosticAssignmentsTable
          assignments={diagnosticAssignments}
          onCancelAssignment={handleCancelDiagnosticAssignment}
          onViewAnswers={handleOpenAnswersModal}
          onAssignForm={handleOpenAssignFormModal}
        />
      </div>

      {/* Diagnostic Answers Modal */}
      {answersModal.isOpen && answersModal.assignment && (
        <div className="cd-modal-overlay" onClick={handleCloseAnswersModal}>
          <div className="cd-modal-content cd-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="cd-modal-header">
              <h2 className="cd-modal-title">Diagnostic Answers</h2>
              <button className="cd-modal-close" onClick={handleCloseAnswersModal}>
                ✕
              </button>
            </div>

            <div className="cd-modal-body">
              {/* Assignment Info */}
              <div className="cd-answers-info">
                <div className="cd-answers-info-row">
                  <span className="cd-answers-label">Form:</span>
                  <span className="cd-answers-value">
                    {answersModal.assignment.template?.title || "—"}
                  </span>
                </div>
                <div className="cd-answers-info-row">
                  <span className="cd-answers-label">Employee:</span>
                  <span className="cd-answers-value">
                    {answersModal.assignment.assignee?.firstName}{" "}
                    {answersModal.assignment.assignee?.lastName}
                  </span>
                </div>
                <div className="cd-answers-info-row">
                  <span className="cd-answers-label">Type:</span>
                  <span className="cd-answers-value">
                    {answersModal.assignment.evaluationType === "self"
                      ? "Self Evaluation"
                      : `Colleague Evaluation of ${answersModal.assignment.target?.firstName} ${answersModal.assignment.target?.lastName}`}
                  </span>
                </div>
                <div className="cd-answers-info-row">
                  <span className="cd-answers-label">Status:</span>
                  <span className="cd-answers-value">
                    {getStatusBadge(answersModal.assignment.status).label}
                  </span>
                </div>
                {submissionData?.submittedAt && (
                  <div className="cd-answers-info-row">
                    <span className="cd-answers-label">Submitted:</span>
                    <span className="cd-answers-value">
                      {new Date(submissionData.submittedAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                )}
              </div>

              {/* Loading State */}
              {submissionLoading && (
                <div className="cd-loading">Loading answers...</div>
              )}

              {/* No Submission */}
              {!submissionLoading && !submissionData && (
                <div className="cd-empty-state">
                  <p>No submission data available.</p>
                </div>
              )}

              {/* Answers List */}
              {!submissionLoading && submissionData && submissionData.answers && (
                <div className="cd-answers-list">
                  {submissionData.answers.map((answer, index) => (
                    <div key={answer._id || index} className="cd-answer-item">
                      <div className="cd-answer-question">
                        <span className="cd-answer-number">Q{answer.questionIndex + 1}.</span>
                        {answer.questionText}
                      </div>
                      <div className="cd-answer-response">
                        <span className="cd-answer-prefix">A:</span>
                        {formatAnswerValue(answer)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="cd-modal-footer">
              <button
                className="cd-modal-btn cd-modal-btn-cancel"
                onClick={handleCloseAnswersModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Diagnostic To Individual Modal */}
      {assignModal && (
        <div className="cd-modal-overlay" onClick={handleCloseAssignModal}>
          <div className="cd-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="cd-modal-header">
              <h2 className="cd-modal-title">Assign Diagnostic to Individual</h2>
              <button className="cd-modal-close" onClick={handleCloseAssignModal}>
                ✕
              </button>
            </div>

            <div className="cd-modal-body">
              {publishedTemplates.length === 0 && (
                <div className="cd-empty-state">
                  <p>
                    No published diagnostic forms are available for this company yet.
                    To assign one, first set it up in the <strong>Diagnostic Forms</strong> section above:
                  </p>
                  <ol style={{ margin: "8px 0 0", paddingLeft: "20px", lineHeight: 1.6 }}>
                    <li>Create a diagnostic form (<strong>+ Add New</strong>).</li>
                    <li>Publish it (set its status to <strong>Published</strong>).</li>
                    <li>Click <strong>Assign →</strong> on that form to assign it to this company.</li>
                  </ol>
                  <p style={{ marginTop: "8px" }}>
                    Then reopen this dialog and the form, employee, and diagnoser dropdowns will appear.
                  </p>
                </div>
              )}

              {publishedTemplates.length > 0 && (
                <>
                  {/* Diagnostic form */}
                  <div className="cd-assign-field">
                    <label className="cd-assign-label">Diagnostic form</label>
                    <select
                      className="cd-select"
                      value={assignForm.templateId}
                      onChange={(e) =>
                        setAssignForm((prev) => ({ ...prev, templateId: e.target.value }))
                      }
                    >
                      <option value="">-- Select a form --</option>
                      {publishedTemplates.map((t) => (
                        <option key={t._id} value={t._id}>
                          {t.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Target employee */}
                  <div className="cd-assign-field">
                    <label className="cd-assign-label">Employee being diagnosed</label>
                    <select
                      className="cd-select"
                      value={assignForm.targetId}
                      onChange={(e) =>
                        setAssignForm((prev) => ({
                          ...prev,
                          targetId: e.target.value,
                          // Drop the target from the diagnoser list if present
                          diagnoserIds: prev.diagnoserIds.filter(
                            (d) => d !== e.target.value
                          ),
                        }))
                      }
                    >
                      <option value="">-- Select an employee --</option>
                      {employees.map((m) => (
                        <option key={memberId(m)} value={memberId(m)}>
                          {m.firstName} {m.lastName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Diagnosers */}
                  <div className="cd-assign-field">
                    <label className="cd-assign-label">Diagnosers (who evaluates them)</label>
                    <div className="cd-assign-diagnosers">
                      {employees.filter((m) => memberId(m) !== assignForm.targetId)
                        .length === 0 && (
                        <p className="cd-answer-empty">No other employees available.</p>
                      )}
                      {employees
                        .filter((m) => memberId(m) !== assignForm.targetId)
                        .map((m) => (
                          <label key={memberId(m)} className="cd-assign-diagnoser-row">
                            <input
                              type="checkbox"
                              className="cd-checkbox"
                              checked={assignForm.diagnoserIds.includes(memberId(m))}
                              onChange={() => toggleDiagnoser(memberId(m))}
                            />
                            <span>
                              {m.firstName} {m.lastName}
                            </span>
                          </label>
                        ))}
                    </div>
                  </div>

                  {/* Self-diagnosis */}
                  <div className="cd-assign-field">
                    <label className="cd-assign-diagnoser-row">
                      <input
                        type="checkbox"
                        className="cd-checkbox"
                        checked={assignForm.includeSelfDiagnosis}
                        onChange={(e) =>
                          setAssignForm((prev) => ({
                            ...prev,
                            includeSelfDiagnosis: e.target.checked,
                          }))
                        }
                      />
                      <span>Include self-diagnosis</span>
                    </label>
                  </div>
                </>
              )}
            </div>

            <div className="cd-modal-footer">
              <button
                className="cd-modal-btn cd-modal-btn-cancel"
                onClick={handleCloseAssignModal}
                disabled={isAssigning}
              >
                Cancel
              </button>
              <button
                className="cd-modal-btn cd-modal-btn-confirm"
                onClick={handleConfirmAssign}
                disabled={isAssigning || publishedTemplates.length === 0}
              >
                {isAssigning ? "Assigning..." : "Assign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Form To Pending Request Modal (approves an employee request) */}
      {assignFormModal.isOpen && assignFormModal.request && (
        <div className="cd-modal-overlay" onClick={handleCloseAssignFormModal}>
          <div className="cd-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="cd-modal-header">
              <h2 className="cd-modal-title">Assign Form to Request</h2>
              <button className="cd-modal-close" onClick={handleCloseAssignFormModal}>
                ✕
              </button>
            </div>

            <div className="cd-modal-body">
              <p className="cd-modal-hint">
                Assign a diagnostic form to{" "}
                <strong>
                  {assignFormModal.request.assignee?.firstName}{" "}
                  {assignFormModal.request.assignee?.lastName}
                </strong>
                's pending request. This approves the request and creates the
                form-fill tasks for the selected diagnosers.
              </p>

              {publishedTemplates.length === 0 ? (
                <div className="cd-empty-state">
                  <p>
                    No published diagnostic forms are assigned to this company yet.
                    Publish a form and assign it to the company (in the Diagnostic
                    Forms section above) first.
                  </p>
                </div>
              ) : (
                <div className="cd-assign-field">
                  <label className="cd-assign-label">Diagnostic form</label>
                  <select
                    className="cd-select"
                    value={assignFormTemplateId}
                    onChange={(e) => setAssignFormTemplateId(e.target.value)}
                  >
                    <option value="">-- Select a form --</option>
                    {publishedTemplates.map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.title}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="cd-modal-footer">
              <button
                className="cd-modal-btn cd-modal-btn-cancel"
                onClick={handleCloseAssignFormModal}
                disabled={isApprovingRequest}
              >
                Cancel
              </button>
              <button
                className="cd-modal-btn cd-modal-btn-confirm"
                onClick={handleConfirmAssignForm}
                disabled={
                  isApprovingRequest ||
                  publishedTemplates.length === 0 ||
                  !assignFormTemplateId
                }
              >
                {isApprovingRequest ? "Assigning..." : "Assign Form"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
